import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';

export function shuffleList(items) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function seededShuffle(items, seed) {
  const result = [...items];
  let state = Math.abs(Number(seed) || 7) || 7;
  for (let i = result.length - 1; i > 0; i -= 1) {
    state = (state * 1664525 + 1013904223) >>> 0;
    const j = state % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function hashSeed(text) {
  let state = 7;
  const value = String(text || '');
  for (let i = 0; i < value.length; i += 1) state = (state * 33 + value.charCodeAt(i)) >>> 0;
  return state || 7;
}

/** Saved deal, or a stable split of the subject bank so exams do not share questions. */
export function questionsForExam(exam, subjectExams, bankIds) {
  const saved = Array.isArray(exam?.questionIds) ? exam.questionIds.filter(Boolean) : [];
  if (saved.length) return saved;
  const ordered = [...(subjectExams || [])]
    .filter((item) => item.subject === exam?.subject)
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0) || String(a.id).localeCompare(String(b.id)));
  const shuffled = seededShuffle(
    [...(bankIds || [])].sort(),
    hashSeed(ordered.map((item) => item.id).join('|')),
  );
  let cursor = 0;
  let assigned = [];
  ordered.forEach((item) => {
    const count = Math.max(0, Number(item.questionCount) || 0);
    const slice = shuffled.slice(cursor, cursor + count);
    cursor += count;
    if (item.id === exam?.id) assigned = slice;
  });
  return assigned;
}

export function listReadyExams(practiceExams, bankIdsBySubject) {
  const rows = [];
  const subjects = [...new Set((practiceExams || []).map((exam) => exam.subject).filter(Boolean))];
  subjects.forEach((subjectId) => {
    const subjectExams = (practiceExams || []).filter((exam) => exam.subject === subjectId);
    const bankIds = bankIdsBySubject?.[subjectId];
    const known = Array.isArray(bankIds);
    subjectExams.forEach((exam) => {
      const assigned = known ? questionsForExam(exam, subjectExams, bankIds) : [];
      const hasQuestions = known ? assigned.length > 0 : Number(exam.questionCount) > 0;
      if (hasQuestions) rows.push({ exam, subjectId });
    });
  });
  return rows;
}

export function cumulativeFromScores(readyRows, scores) {
  if (!readyRows?.length || readyRows.some((row) => !scores?.[row.exam.id])) return null;
  const exams = readyRows.map((row) => {
    const score = scores[row.exam.id];
    return {
      id: row.exam.id,
      subject: row.exam.subject || row.subjectId,
      title: score.examTitle || row.exam.title || 'Practice exam',
      correct: Number(score.correct) || 0,
      total: Number(score.total) || 0,
      percentage: Number(score.percentage) || 0,
    };
  });
  const correct = exams.reduce((sum, exam) => sum + exam.correct, 0);
  const total = exams.reduce((sum, exam) => sum + exam.total, 0);
  const percentage = total ? Math.round((correct / total) * 100) : 0;
  return {
    correct,
    total,
    percentage,
    ready: percentage >= 70,
    exams,
    completedAt: new Date().toISOString(),
  };
}

export function activeRoundScores(data) {
  if (!data) return {};
  if (data.activeScores && Object.keys(data.activeScores).length) return data.activeScores;
  if (data.roundCleared) return {};
  return data.bestScores || {};
}

export function activeRoundAttempts(data) {
  if (!data) return {};
  if (data.activeAttempts && Object.keys(data.activeAttempts).length) return data.activeAttempts;
  if (data.roundCleared) return {};
  return data.attempts || {};
}

export function displayExamScores(data) {
  const active = activeRoundScores(data);
  if (Object.keys(active).length) return active;
  if (Array.isArray(data?.lastReport?.exams) && data.lastReport.exams.length) {
    const map = {};
    data.lastReport.exams.forEach((exam) => {
      map[exam.id] = {
        subject: exam.subject,
        examTitle: exam.title,
        correct: exam.correct,
        total: exam.total,
        percentage: exam.percentage,
        passed: (Number(exam.percentage) || 0) >= 70,
      };
    });
    return map;
  }
  return data?.bestScores || {};
}

export function dealQuestionIds(questionIds, exams) {
  const counts = exams.map((exam) => Number(exam.questionCount) || 0);
  if (!exams.length) throw new Error('Add at least one exam before shuffling.');
  if (counts.some((count) => count < 1)) throw new Error('Each exam needs at least 1 question.');
  const total = counts.reduce((sum, count) => sum + count, 0);
  if (total > questionIds.length) {
    const need = total - questionIds.length;
    throw new Error(`This subject has ${questionIds.length} questions. These exams ask for ${total}. Add ${need} more, or lower the question counts.`);
  }
  const shuffled = shuffleList(questionIds);
  let cursor = 0;
  return exams.map((exam, index) => {
    const dealt = shuffled.slice(cursor, cursor + counts[index]);
    cursor += counts[index];
    return { ...exam, questionIds: dealt };
  });
}

export function tableCells(row) {
  if (Array.isArray(row)) return row;
  return Array.isArray(row?.cells) ? row.cells : [];
}

function letterToIndex(value) {
  if (typeof value === 'number' && value >= 0 && value <= 3) return value;
  const text = String(value ?? '').trim().toUpperCase();
  if (text.length === 1 && text >= 'A' && text <= 'D') return text.charCodeAt(0) - 65;
  const number = Number(text);
  if (Number.isInteger(number) && number >= 0 && number <= 3) return number;
  return 0;
}

function asOption(raw) {
  if (typeof raw === 'string') return { text: raw.trim(), imageUrl: '', imagePath: '', mediaIds: [] };
  return {
    text: String(raw?.text || raw?.label || '').trim(),
    imageUrl: String(raw?.imageUrl || '').trim(),
    imagePath: String(raw?.imagePath || '').trim(),
    mediaIds: Array.isArray(raw?.mediaIds) ? raw.mediaIds : [],
  };
}

export function blankBankQuestion() {
  return {
    title: '',
    skill: '',
    domain: '',
    stem: '',
    passage: '',
    explanation: '',
    exhibit: [],
    options: [0, 1, 2, 3].map(() => ({ text: '', imageUrl: '', imagePath: '' })),
    correct: 0,
  };
}

export function passageFromExhibit(exhibit = []) {
  return exhibit
    .filter((block) => block?.type === 'text' && block.text)
    .map((block) => block.text)
    .join('\n\n');
}

export function imagesFromExhibit(exhibit = []) {
  return exhibit
    .filter((block) => block?.type === 'image' && (block.url || block.mediaId))
    .map((block) => ({ url: block.url || '', path: block.path || '', mediaId: block.mediaId || '' }));
}

export function buildExhibit({ passage = '', tables = [], images = [] }) {
  const exhibit = [];
  if (String(passage).trim()) exhibit.push({ type: 'text', text: String(passage).trim() });
  tables.forEach((rows) => {
    if (rows?.length) exhibit.push({ type: 'table', rows });
  });
  images.forEach((image) => {
    if (image?.url) exhibit.push({ type: 'image', url: image.url, path: image.path || '' });
  });
  return exhibit;
}

export function tablesFromExhibit(exhibit = []) {
  return exhibit
    .filter((block) => block?.type === 'table')
    .map((block) => (block.rows || []).map((row) => tableCells(row)));
}

export function normalizeBankQuestion(raw, index = 0) {
  const options = (Array.isArray(raw?.options) ? raw.options : []).slice(0, 4).map(asOption);
  while (options.length < 4) options.push({ text: '', imageUrl: '', imagePath: '', mediaIds: [] });
  const exhibit = Array.isArray(raw?.exhibit) ? raw.exhibit : [];
  const passage = String(raw?.passage || passageFromExhibit(exhibit) || '').trim();
  return {
    sourceNumber: Number(raw?.sourceNumber || raw?.number || index + 1) || index + 1,
    title: String(raw?.title || '').trim(),
    skill: String(raw?.skill || '').trim(),
    domain: String(raw?.domain || '').trim(),
    stem: String(raw?.stem || raw?.question || '').trim(),
    explanation: String(raw?.explanation || '').trim(),
    passage,
    exhibit: exhibit.length ? exhibit : (passage ? [{ type: 'text', text: passage }] : []),
    options,
    correct: letterToIndex(raw?.correct),
  };
}

export function parseQuestionBankJson(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('That file is not valid JSON.');
  }
  const list = Array.isArray(data) ? data : data?.questions;
  if (!Array.isArray(list) || list.length === 0) {
    throw new Error('JSON must contain a questions array.');
  }
  return list.map((item, index) => normalizeBankQuestion(item, index));
}

function firestoreExhibit(exhibit = []) {
  return exhibit.map((block) => {
    if (block?.type === 'table') {
      return {
        type: 'table',
        rows: (block.rows || []).map((row) => ({ cells: tableCells(row).map((cell) => String(cell || '')) })),
      };
    }
    if (block?.type === 'image') {
      return {
        type: 'image',
        url: String(block.url || ''),
        path: String(block.path || ''),
      };
    }
    return { type: 'text', text: String(block?.text || '') };
  }).filter((block) => {
    if (block.type === 'text') return block.text.trim().length > 0;
    if (block.type === 'image') return block.url;
    return block.rows?.length > 0;
  });
}

export function toFirestoreQuestion(question) {
  const normalized = normalizeBankQuestion(question);
  const options = normalized.options.map((option) => ({
    text: option.text || '',
    imageUrl: option.imageUrl || '',
    imagePath: option.imagePath || '',
  }));
  if (!normalized.stem) throw new Error('A question needs text.');
  if (!options.some((option) => option.text || option.imageUrl)) {
    throw new Error('Add text or an image to at least one option.');
  }
  return {
    sourceNumber: normalized.sourceNumber || 0,
    title: normalized.title,
    skill: normalized.skill,
    domain: normalized.domain,
    stem: normalized.stem,
    explanation: normalized.explanation,
    passage: normalized.passage,
    exhibit: firestoreExhibit(normalized.exhibit),
    options,
    correct: normalized.correct,
  };
}

export function questionsRef(subject) {
  return collection(db, 'questionBanks', subject, 'questions');
}

async function uploadMediaFile(subject, file) {
  const safeName = String(file.name || 'image.png').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `question-banks/${subject}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
  const storageRef = ref(storage, path);
  const bytes = file.bytes || file;
  await uploadBytes(storageRef, bytes, { contentType: file.contentType || file.type || 'image/png' });
  const url = await getDownloadURL(storageRef);
  return { url, path };
}

async function uploadMediaMap(subject, media, onProgress) {
  const uploaded = {};
  const entries = Object.entries(media || {});
  for (let i = 0; i < entries.length; i += 1) {
    const [id, file] = entries[i];
    uploaded[id] = await uploadMediaFile(subject, file);
    if (onProgress) onProgress(`Uploading image ${i + 1} of ${entries.length}`);
  }
  return uploaded;
}

function applyUploadedMedia(question, uploaded) {
  const exhibit = (question.exhibit || []).map((block) => {
    if (block.type !== 'image') return block;
    const file = uploaded[block.mediaId];
    if (!file) return null;
    return { type: 'image', url: file.url, path: file.path };
  }).filter(Boolean);
  const options = question.options.map((option) => {
    const mediaId = option.mediaIds?.[0];
    const file = mediaId ? uploaded[mediaId] : null;
    return {
      text: option.text || '',
      imageUrl: file?.url || option.imageUrl || '',
      imagePath: file?.path || option.imagePath || '',
    };
  });
  return { ...question, exhibit, options };
}

export async function addQuestionsToBank(subject, questions, media = {}, onProgress) {
  if (!questions.length) throw new Error('No questions found in that file.');
  const uploaded = await uploadMediaMap(subject, media, onProgress);
  const prepared = questions.map((question, index) => toFirestoreQuestion(applyUploadedMedia(
    normalizeBankQuestion(question, index),
    uploaded,
  )));
  const chunkSize = 200;
  for (let i = 0; i < prepared.length; i += chunkSize) {
    const batch = writeBatch(db);
    prepared.slice(i, i + chunkSize).forEach((payload) => {
      const refDoc = doc(questionsRef(subject));
      batch.set(refDoc, { ...payload, createdAt: serverTimestamp() });
    });
    await batch.commit();
    if (onProgress) onProgress(`Saved ${Math.min(i + chunkSize, prepared.length)} of ${prepared.length} questions`);
  }
  return prepared.length;
}

export async function uploadQuestionImage(subject, file) {
  if (!file) return null;
  if (file.size > 8 * 1024 * 1024) throw new Error('Image must be under 8 MB.');
  return uploadMediaFile(subject, file);
}

export async function saveBankQuestion(subject, question, questionId) {
  const payload = toFirestoreQuestion(question);
  if (questionId) {
    await updateDoc(doc(db, 'questionBanks', subject, 'questions', questionId), {
      ...payload,
      updatedAt: serverTimestamp(),
    });
    return questionId;
  }
  const created = await addDoc(questionsRef(subject), {
    ...payload,
    createdAt: serverTimestamp(),
  });
  return created.id;
}

function imagePathsFromQuestion(question) {
  const paths = [];
  (question.exhibit || []).forEach((block) => {
    if (block?.path) paths.push(block.path);
  });
  (question.options || []).forEach((option) => {
    if (option?.imagePath) paths.push(option.imagePath);
  });
  return paths;
}

async function deleteStoragePath(path) {
  if (!path || String(path).startsWith('http')) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Could not delete question image', err);
    }
  }
}

export async function deleteAllBankQuestions(subject, questions, onProgress) {
  const list = questions || [];
  const paths = list.flatMap(imagePathsFromQuestion);
  const chunkSize = 400;
  for (let i = 0; i < list.length; i += chunkSize) {
    const batch = writeBatch(db);
    list.slice(i, i + chunkSize).forEach((question) => {
      batch.delete(doc(db, 'questionBanks', subject, 'questions', question.id));
    });
    await batch.commit();
    if (onProgress) onProgress(`Deleted ${Math.min(i + chunkSize, list.length)} of ${list.length} questions`);
  }
  const exams = await getDocs(query(collection(db, 'practiceExams'), where('subject', '==', subject)));
  if (!exams.empty) {
    const batch = writeBatch(db);
    exams.docs.forEach((examDoc) => batch.update(examDoc.ref, { questionIds: [] }));
    await batch.commit();
  }
  for (let i = 0; i < paths.length; i += 1) {
    await deleteStoragePath(paths[i]);
  }
  return list.length;
}

export async function deleteBankQuestion(subject, questionId) {
  await deleteDoc(doc(db, 'questionBanks', subject, 'questions', questionId));
  const exams = await getDocs(query(collection(db, 'practiceExams'), where('subject', '==', subject)));
  const batch = writeBatch(db);
  let changed = false;
  exams.docs.forEach((examDoc) => {
    const ids = examDoc.data().questionIds || [];
    if (!ids.includes(questionId)) return;
    changed = true;
    batch.update(examDoc.ref, { questionIds: ids.filter((id) => id !== questionId) });
  });
  if (changed) await batch.commit();
}

export async function savePracticeExams(subject, drafts) {
  const existing = await getDocs(query(collection(db, 'practiceExams'), where('subject', '==', subject)));
  const kept = new Set(drafts.map((draft) => draft.id).filter(Boolean));
  const batch = writeBatch(db);
  existing.docs.forEach((examDoc) => {
    if (!kept.has(examDoc.id)) batch.delete(examDoc.ref);
  });
  drafts.forEach((draft, index) => {
    const previous = draft.id ? existing.docs.find((examDoc) => examDoc.id === draft.id)?.data() : null;
    const questionCount = Number(draft.questionCount) || 0;
    const dealtIds = Array.isArray(draft.questionIds) ? draft.questionIds : [];
    const sameCount = previous && Number(previous.questionCount) === questionCount;
    const questionIds = dealtIds.length === questionCount
      ? dealtIds
      : (sameCount ? (previous?.questionIds || []) : []);
    const payload = {
      subject,
      title: String(draft.title || '').trim() || `Exam ${index + 1}`,
      timeMinutes: Math.max(1, Number(draft.timeMinutes) || 60),
      questionCount,
      order: index,
      questionIds,
      updatedAt: serverTimestamp(),
    };
    if (draft.id) batch.set(doc(db, 'practiceExams', draft.id), payload, { merge: true });
    else {
      const refDoc = doc(collection(db, 'practiceExams'));
      batch.set(refDoc, { ...payload, createdAt: serverTimestamp() });
    }
  });
  await batch.commit();
}

export async function shufflePracticeExams(subject, drafts, questionIds) {
  const dealt = dealQuestionIds(questionIds, drafts);
  await savePracticeExams(subject, dealt.map((exam) => ({ ...exam, questionIds: exam.questionIds })));
  const saved = await getDocs(query(collection(db, 'practiceExams'), where('subject', '==', subject)));
  const ordered = saved.docs
    .map((examDoc) => ({ id: examDoc.id, ...examDoc.data() }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const batch = writeBatch(db);
  ordered.forEach((exam, index) => {
    batch.update(doc(db, 'practiceExams', exam.id), {
      questionIds: dealt[index]?.questionIds || [],
      questionCount: Number(dealt[index]?.questionCount) || 0,
    });
  });
  await batch.commit();
}
